// src/components/CloudCompute.jsx
import React from "react";
import {
  Cpu,
  HardDrive,
  Gauge,
  Cloud,
  Code2,
  ShieldCheck,
  Network,
  Server,
} from "lucide-react";
import FadeInOnScroll from "./FadeInOnScroll"; // 名字随便，但首字母要大写
import FadeInCard from "./FadeInCard";
import SlideInOnScroll from "./SlideInOnScroll";
import { Link } from "react-router-dom";

export default function CloudCompute() {
  const families = [
    {
      title: "General Purpose",
      desc: "Balanced vCPU, memory and storage for web apps, APIs, and microservices.",
      icon: <Cloud className="w-6 h-6 text-[#7c3aed]" />,
    },
    {
      title: "CPU Optimized",
      desc: "High clock-speed vCPUs for build pipelines, analytics, and edge workloads.",
      icon: <Cpu className="w-6 h-6 text-[#7c3aed]" />,
    },
    {
      title: "Memory Optimized",
      desc: "Large RAM footprints for in-memory databases, caching and search clusters.",
      icon: <HardDrive className="w-6 h-6 text-[#7c3aed]" />,
    },
    {
      title: "Storage Optimized",
      desc: "High throughput and IOPS for data platforms and log-heavy workloads.",
      icon: <Gauge className="w-6 h-6 text-[#7c3aed]" />,
    },
  ];

  const features = [
    {
      title: "Developer-first experience",
      desc: "Instant provisioning with simple dashboards, CLI and fully documented API.",
    },
    {
      title: "Predictable performance",
      desc: "Consistent vCPU and memory performance with sensible noisy-neighbor isolation.",
    },
    {
      title: "Secure by default",
      desc: "Private networking, firewalls, DDoS protection and encrypted volumes.",
    },
    {
      title: "Global connectivity",
      desc: "Region-to-region connectivity and peering to keep latency low for your users.",
    },
  ];

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-purple-50 via-white to-purple-100 text-[#0f0533]">
      {/* 背景柔光 */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 -top-10 h-[26rem] w-[26rem] rounded-full bg-purple-300/25 blur-3xl" />
        <div className="absolute right-[-10rem] top-40 h-[30rem] w-[30rem] rounded-full bg-purple-200/30 blur-3xl" />
        <div className="absolute bottom-[-8rem] left-1/3 h-[22rem] w-[22rem] rounded-full bg-fuchsia-200/30 blur-3xl" />
      </div>

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6 pt-28 pb-28 grid gap-12 md:grid-cols-[1.4fr_1fr] items-center">
        <div>
          <FadeInOnScroll>
            <p className="text-sm tracking-[0.3em] font-semibold text-[#7b5cff] mb-3 uppercase">
              Cloud Compute
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 bg-gradient-to-r from-[#5b21b6] via-[#7c3aed] to-[#a855f7] bg-clip-text text-transparent">
              AI NOVA NX1
              <br />
              Cloud Compute Platform
            </h1>
            <p className="text-base md:text-lg text-[#3b2c70] max-w-xl">
              Run everything from development environments to enterprise
              production workloads on flexible, affordable virtual machines with
              predictable performance and global reach.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="rounded-full bg-gradient-to-r from-[#6a2dd9] to-[#9a8dfc] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_35px_rgba(106,45,217,0.35)] hover:translate-y-[-2px] transition-transform inline-flex items-center justify-center"
              >
                Launch a compute instance
              </Link>
              <p className="text-xs md:text-sm text-[#5b4a99]">
                Start in minutes with hourly or monthly billing.
              </p>
            </div>
          </FadeInOnScroll>
        </div>

        {/* 右侧简易 feature card */}
        {/* <div className="hidden md:block rounded-3xl bg-white/80 backdrop-blur-xl border border-purple-200 p-7 shadow-[0_18px_50px_rgba(148,101,255,0.25)]">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#a855f7] mb-2">
            VX1 INSTANCE FAMILIES
          </p>
          <ul className="space-y-2 text-sm text-[#3b2c70]">
            <li>• General purpose for web apps and APIs</li>
            <li>• CPU optimized for CI/CD and analytics</li>
            <li>• Memory optimized for databases and caching</li>
            <li>• Storage optimized for data platforms</li>
          </ul>
        </div> */}
      </section>

      {/* INSTANCE FAMILIES */}
      <section className="relative py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <FadeInOnScroll>
            <p className="text-xs tracking-[0.3em] text-[#9f7aea] uppercase mb-3 font-semibold">
              Instance families
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2a0f4c] mb-3">
              Choose the right compute for every workload
            </h2>
            <p className="text-sm md:text-base text-[#6b4cae] max-w-2xl mx-auto mb-10">
              Mix and match instance families to support experimentation,
              production applications, and data platforms on a single,
              consistent cloud.
            </p>
          </FadeInOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            {families.map((item, index) => (
              <FadeInCard key={item.title} delay={index * 0.12}>
                <div className="flex flex-col items-start text-left gap-3 rounded-2xl bg-white/85 backdrop-blur-xl border border-purple-100 px-5 py-6 shadow-[0_10px_30px_rgba(148,101,255,0.18)]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#a855f7]/20 via-[#7c3aed]/10 to-[#c4b5fd]/40 flex items-center justify-center">
                    {item.icon}
                  </div>
                  <p className="text-sm font-semibold text-[#3b2c70]">
                    {item.title}
                  </p>
                  <p className="text-xs text-[#4b3a82] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </FadeInCard>
            ))}
          </div>
        </div>
      </section>

      {/* DEV/OPS & RELIABILITY */}
      <section className="relative py-20 bg-gradient-to-b from-purple-50 via-white to-purple-100">
        <div className="max-w-6xl mx-auto px-6 grid gap-10 md:grid-cols-2 items-start">
          <SlideInOnScroll from="left">
            {/* 左：DevOps */}
            <div className="rounded-3xl bg-white/85 backdrop-blur-xl border border-purple-200 p-8 shadow-[0_14px_40px_rgba(148,101,255,0.22)]">
              <div className="flex items-center gap-3 mb-4">
                <Code2 className="w-7 h-7 text-[#7c3aed]" />
                <h3 className="text-xl font-bold text-[#2a0f4c]">
                  Built for modern DevOps
                </h3>
              </div>
              <p className="text-sm text-[#3b2c70] mb-4">
                Use our REST API, Terraform provider, and CLI to integrate
                compute into your existing workflows. Blue-green deploys, canary
                releases, and auto-healing come built-in.
              </p>
              <ul className="text-xs text-[#4b3a82] space-y-2">
                <li>• First-class support for IaC pipelines.</li>
                <li>• Cloud-init and images for common frameworks.</li>
                <li>• Tags, metadata, and audit trails for governance.</li>
              </ul>
            </div>
          </SlideInOnScroll>
          <SlideInOnScroll from="right">
            {/* 右：可靠性 & 网络 */}
            <div className="space-y-6">
              <div className="rounded-3xl bg-white/90 border border-purple-200 p-6 shadow-[0_10px_30px_rgba(148,101,255,0.2)] flex gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-[#7c3aed]" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#2a0f4c] mb-1">
                    Reliability you can trust
                  </h4>
                  <p className="text-xs text-[#4b3a82] leading-relaxed">
                    Redundant power, network and storage layers provide a stable
                    platform for always-on workloads and mission-critical
                    services.
                  </p>
                </div>
              </div>

              <div className="rounded-3xl bg-white/90 border border-purple-200 p-6 shadow-[0_10px_30px_rgba(148,101,255,0.2)] flex gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                  <Network className="w-6 h-6 text-[#7c3aed]" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#2a0f4c] mb-1">
                    Private networking and load balancing
                  </h4>
                  <p className="text-xs text-[#4b3a82] leading-relaxed">
                    Build multi-tier applications using private networks,
                    managed load balancers and VPN/peering connectivity to your
                    existing environments.
                  </p>
                </div>
              </div>
            </div>
          </SlideInOnScroll>
        </div>
      </section>
      {/* SIMPLE CTA */}
      <section className="relative py-16 bg-gradient-to-r from-[#b892ff]/15 via-white to-[#e0d2ff]/35">
        <FadeInOnScroll>
          <div className="max-w-6xl mx-auto px-6 rounded-3xl bg-white/80 border border-purple-200 shadow-[0_20px_60px_rgba(148,101,255,0.25)] flex flex-col md:flex-row items-center justify-between gap-8 px-8 py-10">
            <div className="space-y-2">
              <p className="text-xs tracking-[0.18em] uppercase text-[#9b6fff]">
                Start in minutes
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-[#3d1a6e]">
                Move your workloads to AI NOVA Cloud Compute
              </h3>
              <p className="text-sm text-[#4c3670] max-w-xl">
                Lift-and-shift existing applications or design cloud-native
                architectures with predictable pricing and a developer-friendly
                platform.
              </p>
            </div>
           
            <Link
                          to="/contact"
                          className="rounded-full bg-[#8b5cf6] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#7c3aed] transition"
                        >
                          View Cloud Compute pricing
                        </Link>
          </div>
        </FadeInOnScroll>
      </section>
    </main>
  );
}
